import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TabButton from 'src/components/TabButton';
import { orderDetailsData, orderDetailsLoading, selectHasLegTabs, selectOrderLegActiveTab, selectOrderLegs, setCurrentLegData, setOrderLegActiveTab } from 'src/redux/orders.slice';
import { AppDispatch } from 'src/redux/store';

import CarrierAssignment from './CarrierAssignment';
import LegDocumentsSection from './LegDocumentsSection';
import LineItems from './LineItems';
import OrderDetailsSection from './OrderDetailsSection';
import ShipperAndConsigneeDetails from './ShipperAndConsigneeDetails';
import TimeAndScheduling from './TimeAndScheduling';

const buildAddress = (addr: any): string => {
    if (!addr) return '-';

    return addr?.fullAddress || [addr?.address1, addr?.city, addr?.stateCode, addr?.postal, addr?.country].filter(Boolean).join(', ') || '-';
};

interface IOrderLegDetailsSectionProps {
    isReadOnly?: boolean;
}

const OrderLegDetailsSection = ({ isReadOnly = false }: IOrderLegDetailsSectionProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const order = useSelector(orderDetailsData);
    const isLoading = useSelector(orderDetailsLoading);
    const orderLegs = useSelector(selectOrderLegs);
    // Active tab lives in Redux (single source of truth) — a local useState here desynced
    // from currentLegData on remount (Overview → Messages → Overview) and on refetch.
    const orderLegActiveTab = useSelector(selectOrderLegActiveTab);

    const activeTab = orderLegActiveTab?.tabValue || '';
    const isMasterTab = !!orderLegActiveTab?.isMaster;

    // Master + at least one leg to switch between. False for a single-presented-leg order
    // (a direct order, or a multi-leg order consolidated by Show-as-Multi-Leg OFF).
    const hasLegTabs = useSelector(selectHasLegTabs);

    // The leg driving all leg-tab sections. Matched by numeric leg id — NEVER by legId:
    // WAL's leg-id rule keeps Leg 1's legId equal to the order's orderId, so legId cannot
    // distinguish the master tab from Leg 1. Null on the master tab.
    const activeLegData = useMemo(() => {
        if (isMasterTab) return null;

        return order?.orderLegs?.find((leg: any) => `${leg?.id}` === activeTab) || null;
    }, [order?.orderLegs, activeTab, isMasterTab]);

    const handleTabChange = (e: any) => {
        const tabValue = e?.target?.dataset?.value;
        if (!tabValue || tabValue === activeTab) return;

        const isMaster = tabValue.includes('isMaster-');
        dispatch(setOrderLegActiveTab({ tabValue, isMaster }));

        // currentLegData feeds the order-wide widgets (progress card, header, documents
        // widget) — point it at the selected leg, or back at leg 1 on the master tab.
        if (!isMaster) {
            const matchedLeg = order?.orderLegs?.find((leg: any) => `${leg?.id}` === tabValue);
            if (matchedLeg) dispatch(setCurrentLegData(matchedLeg));
        } else {
            dispatch(setCurrentLegData(order?.orderLegs?.[0] || null));
        }
    };

    // Shipper / Consignee — master tab uses order-level (first-leg shipper / LAST-leg
    // consignee, resolved by the backend), leg tab uses that leg's own parties
    const shipperSource = isMasterTab ? order : activeLegData;
    const consigneeSource = isMasterTab ? order : activeLegData;

    const shipperData = useMemo(
        () => ({
            companyName: shipperSource?.shipper?.companyName || '-',
            address: buildAddress(shipperSource?.shipper?.shipperAddress),
            contactName: shipperSource?.shipper?.contactName || '-',
            phoneNumber: shipperSource?.shipper?.phone || shipperSource?.shipper?.phoneNumber || '-',
            email: shipperSource?.shipper?.email || '-',
        }),
        [shipperSource]
    );

    const consigneeData = useMemo(
        () => ({
            companyName: consigneeSource?.consignee?.companyName || '-',
            address: buildAddress(consigneeSource?.consignee?.consigneeAddress),
            contactName: consigneeSource?.consignee?.contactName || '-',
            phoneNumber: consigneeSource?.consignee?.phone || consigneeSource?.consignee?.phoneNumber || '-',
            email: consigneeSource?.consignee?.email || '-',
        }),
        [consigneeSource]
    );

    // Pickup / Delivery schedule. Leg tab reads the leg's order_leg_schedule row (raw
    // currentPickup*/currentDelivery* keys); master tab reads the order-level rollup the
    // backend derives from the FIRST leg's pickup and the LAST leg's delivery — those come
    // back with PREFIXED keys (pickupStartDate, deliveryStartDate, ...), hence the mapping.
    const schedule = activeLegData?.pickupDeliverySchedule;
    const masterPickup = order?.orderPickupSchedule;
    const masterDelivery = order?.orderDeliverySchedule;

    const pickupData = useMemo(() => {
        const windowType = (isMasterTab ? masterPickup?.pickupWindowType : schedule?.currentPickupWindowType) || 'open';
        const rawStart = (isMasterTab ? masterPickup?.pickupStartTime : schedule?.currentPickupStartTime) || '';
        const rawEnd = (isMasterTab ? masterPickup?.pickupEndTime : schedule?.currentPickupEndTime) || '';
        const bhStart = shipperSource?.shipper?.businessHoursStartTime || '';
        const bhEnd = shipperSource?.shipper?.businessHoursEndTime || '';
        const shouldInjectBH = windowType === 'open' && !rawStart && !!bhStart;

        return {
            startDate: (isMasterTab ? masterPickup?.pickupStartDate : schedule?.currentPickupStartDate) || '',
            endDate: (isMasterTab ? masterPickup?.pickupEndDate : schedule?.currentPickupEndDate) || '',
            startTime: shouldInjectBH ? bhStart : rawStart,
            endTime: shouldInjectBH ? bhEnd : rawEnd,
            timezone: (isMasterTab ? masterPickup?.pickupTimezone : schedule?.pickupTimezone) || '',
            windowType,
        };
    }, [
        isMasterTab,
        masterPickup,
        schedule?.currentPickupStartDate,
        schedule?.currentPickupEndDate,
        schedule?.currentPickupStartTime,
        schedule?.currentPickupEndTime,
        schedule?.pickupTimezone,
        schedule?.currentPickupWindowType,
        shipperSource?.shipper?.businessHoursStartTime,
        shipperSource?.shipper?.businessHoursEndTime,
    ]);

    const deliveryData = useMemo(() => {
        const windowType = (isMasterTab ? masterDelivery?.deliveryWindowType : schedule?.currentDeliveryWindowType || schedule?.initialDeliveryWindowType) || 'open';
        const rawStart = (isMasterTab ? masterDelivery?.deliveryStartTime : schedule?.currentDeliveryStartTime) || '';
        const rawEnd = (isMasterTab ? masterDelivery?.deliveryEndTime : schedule?.currentDeliveryEndTime) || '';
        const bhStart = consigneeSource?.consignee?.businessHoursStartTime || '';
        const bhEnd = consigneeSource?.consignee?.businessHoursEndTime || '';
        const shouldInjectBH = windowType === 'open' && !rawStart && !!bhStart;

        return {
            startDate: (isMasterTab ? masterDelivery?.deliveryStartDate : schedule?.currentDeliveryStartDate) || '',
            endDate: (isMasterTab ? masterDelivery?.deliveryEndDate : schedule?.currentDeliveryEndDate) || '',
            startTime: shouldInjectBH ? bhStart : rawStart,
            endTime: shouldInjectBH ? bhEnd : rawEnd,
            timezone: (isMasterTab ? masterDelivery?.deliveryTimezone : schedule?.deliveryTimezone) || '',
            windowType,
        };
    }, [
        isMasterTab,
        masterDelivery,
        schedule?.currentDeliveryStartDate,
        schedule?.currentDeliveryEndDate,
        schedule?.currentDeliveryStartTime,
        schedule?.currentDeliveryEndTime,
        schedule?.deliveryTimezone,
        schedule?.currentDeliveryWindowType,
        schedule?.initialDeliveryWindowType,
        consigneeSource?.consignee?.businessHoursStartTime,
        consigneeSource?.consignee?.businessHoursEndTime,
    ]);

    return (
        <>
            {/* A single-leg order has only the master tab, so the tab bar offers no choice — it just
                repeats the order id already shown in the page header (client, 2026-10-09). Hidden
                entirely in that case, including while loading: the master tab stays the ACTIVE tab in
                Redux, so every section below keeps reading the same order-level data as before. */}
            {!isLoading && order && hasLegTabs && (
                <div className="sticky top-0 z-[11] global-background-color">
                    <TabButton
                        tabParentClassName="lg:px-1 lg:-mx-1 global-background-color"
                        parentClassName="tab-border-bottom-parent w-full !border-b !border-borderPrimary"
                        activeClassName="tab-border-bottom-active"
                        className="tab-border-bottom min-w-[200px] !pt-[6px]"
                        tabBadgeClassName="tab-border-bottom-badge"
                        tabArray={orderLegs}
                        handleOnClick={handleTabChange}
                        isActive={activeTab}
                    />
                </div>
            )}

            <div className="flex max-lg:flex-col flex-wrap justify-between mt-5 gap-y-4">
                <ShipperAndConsigneeDetails title="Shipper Details" data={shipperData} />
                <ShipperAndConsigneeDetails title="Consignee Details" data={consigneeData} />
            </div>

            <div className="flex max-lg:flex-col max-lg:space-y-4 flex-wrap justify-between mt-4 lg:mt-5">
                <TimeAndScheduling title="Pickup Time & Scheduling" data={pickupData} />
                <TimeAndScheduling title="Delivery Time & Scheduling" data={deliveryData} />
            </div>

            <div className="mt-5">
                <CarrierAssignment isReadOnly={isReadOnly} />
            </div>

            {order?.orderType !== 'ftl' && <LineItems />}

            {/* Order Details is a per-leg section (equipment, ref/PO numbers, quote id). Hidden on
                the master tab of a multi-leg order, where each leg has its own values and the master
                would only show a rollup (same as WAL). With no tab bar there is no leg tab to reach
                it from, so a single-leg order shows it here — OrderDetailsSection reads that one
                leg's own values in that case (client, 2026-10-09). */}
            {(!isMasterTab || !hasLegTabs) && (
                <div className="mt-5">
                    <OrderDetailsSection />
                </div>
            )}

            <div className="mt-5">
                <LegDocumentsSection isReadOnly={isReadOnly} />
            </div>
        </>
    );
};

export default OrderLegDetailsSection;
